// src/features/auth/queries/workspaceRoleQueries.ts
import { useQuery } from '@tanstack/react-query';
import { authQueryKeys } from './authQueries';
import { useAuth } from '../../../context/AuthContext';
import { doc, getDoc } from 'firebase/firestore';
import { db, auth } from '../../../lib/firebase';
import type { WorkspaceRole } from '../types';
import { normalizeWorkspaceRole } from '../utils/workspaceRoleStatus';

// ワークスペースでのユーザーロールを取得するクエリ関数
const fetchWorkspaceRole = async (workspaceId: string, userId: string): Promise<WorkspaceRole | null> => {
  try {
    const memberDocRef = doc(db, `workspaces/${workspaceId}/members/${userId}`);
    const memberSnapshot = await getDoc(memberDocRef);
    
    if (memberSnapshot.exists()) {
      const memberData = memberSnapshot.data();
      const normalized = normalizeWorkspaceRole(memberData.role);
      return normalized;
    }
    
    return null; // メンバーではない
  } catch (error) {
    console.error('❌ ワークスペースロール取得エラー:', {
      error,
      errorCode: (error as any)?.code,
      errorMessage: (error as any)?.message,
      workspaceId,
      userId,
      timestamp: new Date().toISOString()
    });
    return null;
  }
};

/**
 * ワークスペースでのユーザーロールを取得するクエリフック
 */
export const useWorkspaceRoleQuery = (workspaceId: string, enabled: boolean = true) => {
  const { user } = useAuth();
  const uid = user?.uid;

  return useQuery<WorkspaceRole | null>({
    queryKey: [...authQueryKeys.all, 'workspaceRole', workspaceId, uid ?? 'anon'],
    queryFn: () => fetchWorkspaceRole(workspaceId, uid as string),
    enabled: enabled && !!uid,
    staleTime: 5 * 60 * 1000, // 5分
    gcTime: 30 * 60 * 1000,   // 30分
    retry: 1,
  });
};

/**
 * ワークスペース管理者権限をチェックするクエリフック
 */
export const useIsWorkspaceAdminQuery = (workspaceId: string, enabled: boolean = true) => {
  const { data: role, ...rest } = useWorkspaceRoleQuery(workspaceId, enabled);
  
  return {
    ...rest,
    data: role === 'owner' || role === 'admin',
  };
};

/**
 * 複数のワークスペースでの管理者権限を一括チェックするクエリフック
 */
export const useMultipleWorkspaceAdminQuery = (workspaceIds: string[], enabled: boolean = true) => {
  const { user } = useAuth();
  const uid = user?.uid;

  return useQuery<Record<string, boolean>>({
    queryKey: [...authQueryKeys.all, 'multipleWorkspaceAdmin', workspaceIds.sort(), uid ?? 'anon'],
    queryFn: async () => {
      if (!uid) return {};
      
      const results: Record<string, boolean> = {};
      const promises = workspaceIds.map(async (workspaceId) => {
        const role = await fetchWorkspaceRole(workspaceId, uid);
        results[workspaceId] = role === 'owner' || role === 'admin';
      });
      
      await Promise.all(promises);
      return results;
    },
    enabled: enabled && !!uid && workspaceIds.length > 0,
    staleTime: 5 * 60 * 1000, // 5分
    gcTime: 30 * 60 * 1000,   // 30分
    retry: 1,
  });
};

